import {
    Component,
    OnInit,
    Input,
    ChangeDetectorRef,
    ViewChild,
    Output,
    OnDestroy
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, AbstractControl } from '@angular/forms';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { ResponseStatus } from '../../shared';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { TransitionService, Transition } from '@uirouter/core';
import {
    Table,
    Accordion,
    SortEvent,
    SelectItem
} from '../../shared/efaact-primeng.module';
import { UserSetting } from '../store';
import { Process } from '../../shared';
import { ReplaySubject, Subject, merge } from 'rxjs';
import moment from 'moment';
import * as fromShared from '../../shared';
import * as fromLookups from '../../lookups';
import { Color, ITimeZone } from '../../lookups';
import { takeWhile, throttleTime, debounceTime, filter } from 'rxjs/operators';

@Component({
    selector: 'ef-user-setting',
    templateUrl: './user-setting.component.html',
    styleUrls: ['./user-setting.component.scss'],
    standalone: false
})
export class UserSettingComponent implements OnInit, OnDestroy {
    public loading = true;
    private active = true;
    private _settings: Array<UserSetting>;
    userSettingsForm: UntypedFormGroup;
    passwordForm: UntypedFormGroup;
    protected openTabs: Array<number> = [];
    public editing: UserSetting;
    rowGroupMetadata: any;
    private TextboxChangedSubject = new ReplaySubject<{
        value: string;
        data: UserSetting;
    }>(1);
    private EditCompleteSubject = new ReplaySubject<{
        value: string;
        data: UserSetting;
    }>(1);
    private SettingChangedSubject = new ReplaySubject<{
        value: string;
        data: UserSetting;
    }>(1);
    @Output() public Save = new Subject<UserSetting>();
    @ViewChild('settingsAccordion') settingsEl: Accordion;
    @ViewChild('dtSettings') settingsTable: Table;
    @Input() colors: Color[];
    @Input() timeZones: Array<ITimeZone>;
    @Input() public set settings(value: Array<UserSetting>) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:settings',
            value,
            settingsEl: this.settingsEl
        });
        this.loading = false;
        if (value && value.length) {
            this.userSettingsForm = new UntypedFormGroup({});
            this._settings = value
                .sort((a, b) => a.CatID - b.CatID)
                .map(x => {
                    this.userSettingsForm.addControl(
                        x.EmpSettingType,
                        new UntypedFormControl(x.EmpSettingValue)
                    );
                    return {
                        ...x,
                        EmpSettingValue:
                            x.CtrlType.toLocaleLowerCase() === 'datetimepicker'
                                ? moment(x.EmpSettingValue).toDate()
                                : x.EmpSettingValue
                    };
                });
        }
        this.updateRowGroupMetaData(value);
    }
    public get settings(): Array<UserSetting> {
        return this._settings
            ? this._settings.sort((a, b) => a.CatID - b.CatID)
            : [];
    }
    @Input() public set categories(
        value: { CatID: number; Description: string }[]
    ) {
        if (value && value.length > 0) {
            this.categoryItems = value
                .sort((a, b) => a.CatID - b.CatID)
                .map(x => ({
                    value: x.CatID,
                    label: x.Description
                }));
        }
    }
    categoryItems: SelectItem[];
    private tUnregister: Function;
    private me: UserSettingComponent;
    moment: any;
    constructor(
        private log: LoggingService,
        private cdRef: ChangeDetectorRef,
        public confirmservice: UnsavedChangesService,
        public transitionService: TransitionService
    ) {
        this.moment = moment;
        this.passwordForm = new UntypedFormGroup({
            password: new UntypedFormControl()
        });
        this.userSettingsForm = new UntypedFormGroup({});
        this.tUnregister = transitionService.onStart(
            { from: 'usersetting' },
            this.uiCanExit.bind(this)
        );
        this.me = this;
    }

    ngOnInit() {
        this.setupSubscriptions();
    }
    ngOnDestroy() {
        this.active = false;
        this.tUnregister();
    }
    uiCanExit(transition?: Transition) {
        function getControlName(c: AbstractControl): string | null {
            const formGroup = c.parent.controls;
            return (
                Object.keys(formGroup).find(name => c === formGroup[name]) ||
                null
            );
        }
        let result = true;
        this.log.verbose({
            type: 'UserSettingPresenterComponent:uiCanExit',
            userSettingsForm: this.userSettingsForm,
            transition,
            controls: this.userSettingsForm.controls
        });
        // there should only be one dirty item possible
        // TODO: check for form dirty and save if it is.
        const keys = Object.keys(this.userSettingsForm.controls);
        return new Promise((r, e) => {
            keys.forEach(key => {
                const c = this.userSettingsForm.controls[key];
                const setting = this.settings.find(
                    x => x.EmpSettingType === key
                );
                if (c.dirty) {
                    this.log.info({
                        type: 'UserSettingPresenterComponent:uiCanExit',
                        value: 'dirty control',
                        key,
                        c
                    });
                    if (
                        setting.Validation &&
                        setting.Validation === 'confirm'
                    ) {
                        this.log.warn({
                            type: 'UserSettingPresenterComponent:uiCanExit',
                            value: 'validation control',
                            key,
                            c
                        });
                        result = false;
                        return this.confirmservice
                            .confirmPromise({
                                dialogText: `Are you sure you want to update ${setting.EmpSettingTypeLabel}`
                            })
                            .then(() => {
                                this.settingChanged(c.value, setting);
                                result = true;
                                r(result);
                                // result = result && true;
                            })
                            .catch(() => {
                                result = false;
                                r(result);
                            });
                    } else {
                        this.settingChanged(c.value, setting);
                        // result = result && true;
                    }
                } else {
                    this.log.verbose({
                        type: 'UserSettingPresenterComponent:uiCanExit',
                        value: 'clean control',
                        key,
                        c
                    });
                }
            });
            if (result) {
                r(true);
            }
        });
    }
    getDescriptionForDropdown(settingType, settingValue) {
        const options = this.getValuesForDropdown(settingType);
        if (settingType.toLocaleLowerCase().includes('color')) {
            return this.colors.find(x => x.Colorhex === settingValue).Colorname;
        }
        switch (settingType.toLocaleLowerCase()) {
            case 'timezone':
                return this.timeZones.find(x => x.DisplayName === settingValue)
                    ? this.timeZones.find(x => x.DisplayName === settingValue)
                          .DisplayName
                    : settingValue;
        }
        return settingValue;
    }
    getValuesForDropdown(settingType: string) {
        if (settingType.toLocaleLowerCase().includes('color')) {
            return this.colors.map(x => ({
                label: x.Colorname,
                value: x.Colorhex
            }));
        }
        switch (settingType.toLocaleLowerCase()) {
            case 'timezone':
                return this.timeZones.map(x => ({
                    label: x.DisplayName,
                    value: x.StandardName
                }));
        }
        return [];
    }
    protected settingChanged(value, setting) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:settingChanged',
            value,
            setting
        });
        // const newVal = $event.value || $event;
        // const setting = this.settings.find(
        //     x => x.SettingType === item.SettingType
        // );
        this.updateSetting(value, setting);
    }
    updateSetting(value, setting: UserSetting) {
        const saveSetting = (s: UserSetting, v) => {
            s.Status = {
                StatusCode: ResponseStatus.Loading,
                Message: 'Saving'
            };
            s.EmpSettingValue = v;
            this.loading = true;
            this.Save.next(s);
            // this.editing = null;
        };
        this.log.verbose({
            type: 'UserSettingPresenterComponent:updateSetting',
            value,
            setting
        });
        if (setting.Validation && setting.Validation.includes('confirm')) {
            this.confirmservice
                .confirmPromise({
                    dialogText: `Are you sure you want to update ${setting.EmpSettingTypeLabel}`
                })
                .then(() => {
                    saveSetting.bind(this.me)(setting, value);
                    return;
                })
                .catch(e => {
                    this.log.verbose({
                        type:
                            'UserSettingPresenterComponent:updateSetting:confirm:catch',
                        e,
                        setting,
                        control: this.userSettingsForm.controls[
                            setting.EmpSettingType
                        ].value
                    });
                    setting.EmpSettingValue = this.editing.EmpSettingValue;
                    this.userSettingsForm.controls[
                        setting.EmpSettingType
                    ].reset(setting.EmpSettingValue);
                    this.userSettingsForm.controls[
                        setting.EmpSettingValue
                    ].setValue(setting.EmpSettingValue);
                    this.cdRef.detectChanges();
                });
        } else {
            saveSetting.bind(this.me)(setting, value);
        }
    }
    textboxChanged($event, data) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:textboxChanged',
            $event,
            data
        });
        this.TextboxChangedSubject.next({ value: $event, data });
    }
    textboxBlur($event, data: UserSetting) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:textboxBlur',
            $event,
            data
        });

        if (
            this.editing &&
            this.editing.EmpSettingValue !== data.EmpSettingValue
        ) {
            this.SettingChangedSubject.next({ value: $event, data: data });
            // this.updateSetting($event, data);
        }
    }
    checkboxChanged($event, data) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:checkboxChanged',
            $event,
            data
        });
        this.SettingChangedSubject.next({ value: $event.checked, data });
    }
    dropdownChanged($event, data) {
        this.SettingChangedSubject.next({ value: $event.value, data });
    }
    setupSubscriptions() {
        this.SettingChangedSubject.pipe(
            takeWhile(() => this.active),
            throttleTime(100)
        ).subscribe(x => {
            this.log.verbose({
                type: 'UserSettingPresenterComponent:SettingChangedSubject',
                x
            });
            this.settingChanged(x.value, x.data);
        });

        const latestChange = merge(
            this.TextboxChangedSubject.pipe(
                debounceTime(3000),
                filter(x => !x.data.Validation)
            ),
            this.EditCompleteSubject,
            2
        ).pipe(
            takeWhile(() => this.active),
            throttleTime(100)
        );

        latestChange.subscribe(z => {
            this.log.verbose({
                type: 'UserSettingPresenterComponent:latestChange(z)',
                z
            });
            this.SettingChangedSubject.next(z);
        });
    }
    onSave($event) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onSave',
            $event
        });
        this.Save.next($event);
    }
    onEditInit($event: { data: UserSetting }) {
        const setting = this.settings.find(
            x => x.EmpSettingType === $event.data.EmpSettingType
        );
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onEditInit',
            $event,
            editing: this.editing,
            setting
        });
        // if (setting.CtrlType !== 'checkbox') {
        this.editing = { ...$event.data };
        // }
        if (
            (!!this.editing &&
                this.editing.EmpSettingType !== $event.data.EmpSettingType) ||
            setting.CtrlType === 'checkbox'
        ) {
            this.onEditComplete({ data: this.editing || $event.data });
        }
    }
    onEditComplete($event: { data: Partial<UserSetting> }) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onEditComplete',
            $event
        });
        const setting = this.settings.find(
            x => x.EmpSettingType === $event.data.EmpSettingType
        );
        const ctrlType = setting.CtrlType.toLocaleLowerCase();

        let newVal = setting.EmpSettingValue;
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onEditComplete',
            $event,
            setting,
            editing: this.editing,
            newVal
        });
        if (newVal === this.editing.EmpSettingValue) {
            return;
        }
        if (ctrlType === 'datetimepicker') {
            newVal = moment(setting.EmpSettingValue).format('MM/DD/YYYY');
            this.SettingChangedSubject.next({ value: newVal, data: setting });
        }
        if (ctrlType === 'textbox' || ctrlType === 'password') {
            this.SettingChangedSubject.next({ value: newVal, data: setting });
        }
    }
    onDatepickerClose($event) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onDatepickerClose',
            $event,
            editing: this.editing
        });
    }
    customSort(event: SortEvent) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:customSort',
            event
        });
        event.data.sort((a, b) => {
            const result = a.CatDescription.toLocaleLowerCase().localeCompare(
                b.CatDescription.toLocaleLowerCase()
            );
            return result === 0
                ? a[event.field]
                      .toLocaleLowerCase()
                      .localeCompare(b[event.field].toLocaleLowerCase())
                : result;
        });
    }
    filterTable(value, column, operator) {
        // dtSettings.filter($event.value.Description, 'CatDescription', 'in')
        this.log.verbose({
            type: 'UserSettingPresenterComponent:filterTable',
            value,
            column,
            operator
        });
        this.settingsTable.filter(value, column, operator);
    }
    onFilter($event) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onFilter',
            $event
        });
        // setTimeout(() => {
        this.onUpdateRowGroupMetaData($event.filteredValue);
        // }, 50);
    }
    onSort($event) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:onSort',
            $event
        });
        setTimeout(() => {
            this.onUpdateRowGroupMetaData(
                this.settingsTable.filteredValue || this.settingsTable.value
            );
        }, 50);
    }
    onUpdateRowGroupMetaData(data) {
        this.updateRowGroupMetaData(data);
    }

    updateRowGroupMetaData(data) {
        this.log.verbose({
            type: 'UserSettingPresenterComponent:updateRowGroupMetaData',
            data
        });
        this.rowGroupMetadata = {};
        if (data) {
            for (let i = 0; i < data.length; i++) {
                const rowData = data[i];
                const category = rowData.CatID;
                if (i === 0) {
                    this.rowGroupMetadata[category] = { index: 0, size: 1 };
                } else {
                    const previousRowData = data[i - 1];
                    const previousRowGroup = previousRowData.CatID;
                    if (category === previousRowGroup) {
                        this.rowGroupMetadata[category].size++;
                    } else {
                        this.rowGroupMetadata[category] = { index: i, size: 1 };
                    }
                }
            }
            this.log.verbose({
                type: 'UserSettingPresenterComponent:updateRowGroupMetaData',
                data,
                rowGroupMetadata: this.rowGroupMetadata
            });
            this.cdRef.markForCheck();
        }
    }
}
